/**
 * Invoicing Delete Policy
 *
 * Ported from `erpsDeletePolicy`. Reacts when a deletion starts
 * (`delete/start` flips `loading.submitting` false→true and snapshots the
 * target id into `state.pendingDeletion`), removes the provisioned invoicing
 * integration, and resets on success/error.
 *
 * As in `erps`, there is no backend delete command for a provisioned
 * `SystemIntegration` yet, so the removal is optimistic — the list-load
 * subscription is the source of truth and will reconcile. The backend call is
 * left as a TODO to be wired when a deprovision command ships.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { InvoicingState } from '../state';
import { InvoicingCommand } from '../commands';

/**
 * Creates a policy that handles invoicing-integration deletion.
 *
 * @param _backend - Client library instance (reserved for the deprovision
 *   command once it exists).
 */
export const createInvoicingDeletePolicy = (
  _backend: ClientLibraryType
): Policy<InvoicingState, InvoicingCommand> => ({
  name: 'invoicing-delete',

  // React only when a *deletion* starts (pendingDeletion set), so a concurrent
  // `submit/start` — which also flips `loading.submitting` — does not trigger
  // this policy.
  shouldReact: ( prev, next ) =>
    !prev.loading.submitting &&
    next.loading.submitting &&
    next.pendingDeletion !== null,

  onStateChange: async ({ next, ctx }) => {
    const deletion = next.pendingDeletion;

    if ( !deletion || !deletion.integrationId ) {
      console.error( '[invoicing] Delete called without integration id' );
      ctx.dispatch(
        { type: 'integrations/invoicing/delete/error', payload: {}},
        { source: 'api' }
      );
      return;
    }

    const { integrationId } = deletion;

    try {
      // TODO: call the backend deprovision command for a provisioned
      // SystemIntegration once it exists. Until then the removal is optimistic
      // and reconciled by the list-load subscription.
      console.warn( '[invoicing] Backend deprovision command not implemented yet' );

      ctx.dispatch(
        { type: 'integrations/invoicing/delete/success', payload: { integrationId }},
        { source: 'api' }
      );
    } catch ( error ) {
      console.error( '[invoicing] Delete failed:', error );
      ctx.dispatch(
        { type: 'integrations/invoicing/delete/error', payload: {}},
        { source: 'api' }
      );
    }
  }
});
